import { Link } from 'react-router-dom';
import {
  Smartphone, BatteryCharging, PlugZap, Camera, Droplets, Cpu,
  CheckCircle2, Zap, ShieldCheck, Clock, MapPin, Star,
} from 'lucide-react';
import Seo from '../../components/Seo';
import ServicePageLayout from '../../components/services/ServicePageLayout';
import { ServiceSection, ServiceFAQ, RelatedServices } from '../../components/services/ServiceUI';
import { ServiceHeroPro, ProofStrip, DiagnosisList, PriceCardPro, AvisStrip, LocalCTA } from '../../components/services/elevated';
import DevisSection from '../../components/DevisSection';

const SLUG = '/reparation-iphone-montpellier';

const faq = [
  {
    q: "Faut-il prendre rendez-vous pour réparer son iPhone ?",
    a: "Non. Vous pouvez passer directement à la boutique du lundi au samedi, de 10h à 20h. La plupart des réparations sont faites pendant que vous attendez.",
  },
  {
    q: "Combien de temps prend la réparation d'un iPhone ?",
    a: "La majorité des réparations (écran, batterie) sont réalisées en environ 30 minutes. Les interventions de micro-soudure ou de désoxydation demandent davantage de temps.",
  },
  {
    q: "Vais-je perdre mes données ?",
    a: "Non, une réparation d'écran ou de batterie ne touche pas à vos données. En cas de panne grave, nous proposons aussi la récupération de données.",
  },
  {
    q: "Quelle garantie est appliquée ?",
    a: "Toutes nos réparations sont garanties 6 mois.",
  },
];

const pannes = [
  {
    icon: Smartphone,
    title: "Écran cassé, tactile HS",
    desc: (
      <>Remplacement de vitre et de dalle — voir notre page{' '}
        <Link to="/reparation-ecran-iphone-montpellier" className="text-blue-600 font-medium hover:underline">réparation d'écran iPhone</Link>.
      </>
    ),
  },
  {
    icon: BatteryCharging,
    title: "Batterie qui lâche",
    desc: (
      <>Se vide vite, s'éteint ou gonfle — <Link to="/changement-batterie-iphone-montpellier" className="text-blue-600 font-medium hover:underline">changement de batterie iPhone</Link>.</>
    ),
  },
  { icon: PlugZap, title: "iPhone qui ne charge plus", desc: "Nettoyage ou remplacement du connecteur de charge." },
  { icon: Camera, title: "Photo, Face ID, boutons", desc: "Appareil photo, Face ID, haut-parleur ou micro défectueux." },
  { icon: Droplets, title: "Tombé dans l'eau", desc: "Désoxydation et séchage de la carte mère." },
  {
    icon: Cpu,
    title: "Ne s'allume plus",
    desc: (
      <>Reste en boucle ou écran noir — <Link to="/micro-soudure-montpellier" className="text-blue-600 font-medium hover:underline">micro-soudure</Link> de la carte mère.</>
    ),
  },
];

const reasons = [
  "En plein centre-ville, place des Martyrs de la Résistance, sans rendez-vous.",
  "Réparation express : la plupart des interventions en 30 minutes.",
  "Devis gratuit avant toute intervention, sans engagement.",
  "Garantie 6 mois sur les réparations.",
  "Techniciens expérimentés, y compris en micro-soudure.",
];

const reviews = [
  { name: 'M. Rico', text: "Super expérience ! Venue pour un écran complètement noir, rien à redire. Très professionnel et de bons conseils." },
  { name: 'Alexandre Launes', text: "Magasin honnête : je pensais mon écran HS, c'était juste la nappe de connexion, ils me l'ont dit. Prix plus bas qu'ailleurs." },
  { name: 'Christophe Farque', text: "Gentillesse, professionnalisme, rapidité, explications claires et petits prix. Merci à l'équipe !" },
];

const proofItems = [
  { icon: Zap, label: 'Réparé en 30 min' },
  { icon: ShieldCheck, label: 'Garantie 6 mois' },
  { icon: Clock, label: 'Sans rendez-vous' },
  { icon: Star, label: '4,7/5 · 579 avis' },
  { icon: MapPin, label: 'Centre-ville Montpellier' },
];

const ReparationIphone = () => {
  return (
    <ServicePageLayout trail={[{ name: 'Accueil', to: '/' }, { name: 'Réparation iPhone' }]}>
      <Seo
        title="Réparation iPhone Montpellier | AlloRéparations"
        description="Réparation iPhone à Montpellier centre : écran, batterie, micro-soudure. Devis gratuit, 30 min, garantie 6 mois, sans rendez-vous."
        canonicalPath={SLUG}
        service={{
          name: "Réparation iPhone à Montpellier",
          serviceType: "Réparation de smartphone iPhone",
          description: "Réparation d'iPhone à Montpellier : écran, batterie, connecteur de charge, désoxydation et micro-soudure de carte mère.",
        }}
        faq={faq}
        breadcrumb={[
          { name: 'Accueil', path: '/' },
          { name: 'Réparation iPhone à Montpellier', path: SLUG },
        ]}
      />

      <ServiceHeroPro
        h1Before="Réparation "
        h1Highlight="iPhone"
        h1After=" à Montpellier"
        intro="AlloRéparations répare votre iPhone à Montpellier centre-ville, le plus souvent en 30 minutes et sans rendez-vous. Écran cassé, batterie qui lâche, panne de charge, dégât des eaux ou problème de carte mère : nous intervenons sur tous les modèles, de l'iPhone 6 à l'iPhone 15, avec devis gratuit et garantie de 6 mois."
        image={{ src: '/iphones/iphone-13-256go-minuit.jpg', alt: "Réparation iPhone à Montpellier — écran et batterie iPhone" }}
        rating={{ value: '4,7', count: '579' }}
        hasWidget
      />

      <ProofStrip items={proofItems} />

      <DiagnosisList
        title="Les pannes iPhone que nous réparons"
        intro="Notre atelier prend en charge l'ensemble des pannes courantes et complexes de l'iPhone."
        items={pannes}
      />

      <ServiceSection title="Tous les modèles d'iPhone">
        <p>
          Nous réparons toutes les générations : iPhone 15, 14, 13, 12, 11, XR, XS, X, SE, 8 et antérieurs.
          L'iPhone 11 reste le modèle le plus apporté en boutique à Montpellier ; nous gardons les pièces des
          modèles les plus courants en stock pour une réparation le jour même.
        </p>
      </ServiceSection>

      <ServiceSection title="Pourquoi choisir AlloRéparations à Montpellier">
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
          {reasons.map((r, i) => (
            <div key={i} className="flex items-start gap-3">
              <CheckCircle2 size={20} className="text-emerald-500 flex-shrink-0 mt-0.5" />
              <span className="text-slate-700">{r}</span>
            </div>
          ))}
        </div>
      </ServiceSection>

      <PriceCardPro
        eyebrow="Tarifs de référence"
        title="Combien coûte une réparation d'iPhone à Montpellier ?"
        columns={['Réparation', 'Délai', 'Prix TTC']}
        rows={[
          ['Réparation écran iPhone', '30 min', '45–195 €'],
          ['Changement de batterie', '30 min', '25–99 €'],
          ['Connecteur de charge', '30–60 min', '45–79 €'],
          ['Désoxydation / dégât des eaux', '24–48 h', 'sur devis'],
          ['Micro-soudure carte mère', 'sur devis', 'sur devis'],
        ]}
        hasWidget
      />

      {/* Widget de devis réutilisé (réparation + rachat) */}
      <DevisSection />

      <AvisStrip rating="4,7" count="579" reviews={reviews} />

      <ServiceFAQ faq={faq} />

      <LocalCTA
        heading="Un souci sur votre iPhone ? Passez en boutique."
        sub="Diagnostic et devis gratuits, réparation express en 30 minutes, sans rendez-vous."
      />

      <RelatedServices
        links={[
          { label: "Réparation d'écran iPhone", to: '/reparation-ecran-iphone-montpellier', desc: 'Vitre, dalle, tactile — dès 45 €.' },
          { label: 'Changement de batterie iPhone', to: '/changement-batterie-iphone-montpellier', desc: 'Autonomie restaurée en 30 min.' },
          { label: 'Micro-soudure & carte mère', to: '/micro-soudure-montpellier', desc: "iPhone qui ne s'allume plus, charge HS." },
          { label: 'Rachat & reprise de votre iPhone', to: '/#devis', desc: 'Estimez la valeur de votre iPhone.' },
        ]}
      />
    </ServicePageLayout>
  );
};

export default ReparationIphone;
